/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: UsersPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main users management page with data grid, CRUD operations,
    search functionality, and user management capabilities.

 ?? Notes:
    - Refactored to use modular hooks and components
    - Integrates DataGridSimple for user listing and management
    - Supports user creation, editing, deletion, and duplication
    - Implements search and filtering functionality
    - Uses confirmation modals for destructive actions

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Users } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModulePageContainer } from '../../components/layouts';
import { deleteUserById, navigateToUserDuplicate } from './usersActions';
import { useUsersList } from './hooks';
import { useUsersColumnRenderers } from './components/UsersColumns';
import { UsersActions } from './components/UsersActions';
import { User } from './types';

export default function UsersPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const tenantSlug = user?.tenant?.slug || t('users.defaultTenant');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear page title to show tenant logo + name in header
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Use hooks for data grid (includes columns internally)
  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useUsersList();

  // Column renderers
  const { getColumnRenderer } = useUsersColumnRenderers();

  // Handle create user
  const handleCreateUser = useCallback(() => {
    navigate('/users/new');
  }, [navigate]);

  // Handle edit user
  const handleEditUser = useCallback((user: User) => {
    navigate(`/users/${user.uuid ?? user.id}`);
  }, [navigate]);

  // Handle duplicate user
  const handleDuplicateUser = useCallback((user: User) => {
    navigateToUserDuplicate(navigate, user.uuid ?? user.id);
  }, [navigate]);

  // Handle delete user
  const handleDeleteUser = useCallback((user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      await deleteUserById(userToDelete.id, tenantSlug, user?.id);

      setShowDeleteModal(false);
      setUserToDelete(null);
      showSuccess(t('users.userDeleted'));

      // Refresh data
      refresh();
    } catch (error) {
      // Check if it's the primary admin error
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('primary administrator') ||
        errorMessage.includes('PRIMARY_ADMIN_CANNOT_BE_DELETED')) {
        showError(t('users.errors.primaryAdminCannotBeDeleted'));
      } else {
        showError(t('users.errors.errorDeleting'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [userToDelete, tenantSlug, user?.id, showSuccess, showError, t, refresh]);

  // Render actions
  const renderActions = useCallback(
    (user: User) => (
      <UsersActions
        user={user}
        onEdit={handleEditUser}
        onDelete={handleDeleteUser}
      />
    ),
    [handleEditUser, handleDeleteUser],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          titleIcon={<Users className="h-5 w-5 text-primary" />}
          data={data}
          columns={columns.map((col) => ({
            ...col,
            render: getColumnRenderer(col.key),
          }))}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreateUser}
          onEdit={handleEditUser}
          onDuplicate={handleDuplicateUser}
          onDelete={handleDeleteUser}
          title={t('users.users')}
          addButtonText={t('users.add')}
          searchPlaceholder={t('users.searchUsers')}
          emptyMessage={t('users.noUsersFound')}
          deleteTitle={t('users.deleteUserTitle')}
          deleteMessage={(item) => {
            if (!item || !item.username) {
              return t('users.areYouSureDeleteUser') + "?";
            }
            return `${t('users.areYouSureDeleteUser')} "${item.username}"?`;
          }}
          renderActions={renderActions}
          translations={{
            items: t('common:items'),
            columns: t('common:columns'),
            noItemsFound: t('common:noItemsFound'),
            showing: t('common:showing'),
            to: t('common:to'),
            of: t('common:of'),
            results: t('common:results'),
            previous: t('common:previous'),
            next: t('common:next'),
            page: t('common:page'),
            deleteItem: t('common:deleteItem'),
            areYouSureDeleteItem: t('common:areYouSureDeleteItem'),
            delete: t('common:delete'),
            cancel: t('common:cancel'),
            allSelected: t('common:allSelected'),
            selected: t('common:selected'),
            searchOptions: t('common:searchOptions'),
            noOptionsFound: t('common:noOptionsFound'),
            selectAll: t('common:selectAll'),
            clearAll: t('common:clearAll')
          }}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('users.deleteUserTitle')}
        message={userToDelete ? `${t('users.areYouSureDeleteUser')} "${userToDelete.username}"?` : ''}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        isLoading={isDeleting}
        type="danger"
      />

    </ModulePageContainer>
  );
}
